// CRB10: personal preferences and live governed results, never a cached figure.
import type { CustomReportResultDto } from "./api.ts";
import { compareCells, dateSpanText, formatCell, type BuilderState } from "./customReportModel.ts";
interface Reply<T> {
  status: number;
  body: T & { message?: string };
}
interface Tile {
  id: string;
  available: boolean;
  definitionId?: string;
  version?: number;
  label?: string;
}
export interface PersonalTilesHost {
  api<T>(method: "GET" | "POST", suffix: string, body?: unknown): Promise<Reply<T>>;
  active(): boolean;
  canManage: boolean;
  openSaved(definitionId: string, version: number): void;
}
function node<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  text?: string,
): HTMLElementTagNameMap[K] {
  const element = document.createElement(tag);
  if (text !== undefined) element.textContent = text;
  return element;
}
function button(text: string): HTMLButtonElement {
  const control = node("button", text);
  control.type = "button";
  control.className = "cr-btn-quiet";
  return control;
}
export function tileSpecControl(
  state: BuilderState,
  locked: boolean,
  changed: (next: BuilderState) => void,
): HTMLElement {
  const section = node("section");
  section.className = "cr-sec";
  const label = node("label", "Dashboard tile");
  const check = node("input");
  check.type = "checkbox";
  check.checked = state.tile !== undefined;
  const eligible = state.dimensions.length === 0 && state.measures.length === 1;
  check.disabled = locked || (!eligible && state.tile === undefined);
  label.prepend(check);
  section.append(label);
  check.onchange = () => {
    const next = { ...state };
    if (check.checked) next.tile = { label: state.title.trim() || "Report" };
    else delete next.tile;
    changed(next);
  };
  if (state.tile !== undefined) {
    const name = node("input");
    name.type = "text";
    name.maxLength = 120;
    name.value = state.tile.label;
    name.disabled = locked;
    name.setAttribute("aria-label", "Tile label");
    name.oninput = () => {
      if (state.tile !== undefined) state.tile.label = name.value;
    };
    section.append(name);
  }
  return section;
}
export function mountPersonalTiles(hostElement: HTMLElement, host: PersonalTilesHost): void {
  const section = node("section");
  section.className = "cr-personal-tiles";
  section.hidden = true;
  hostElement.append(section);
  let operation = 0;
  const active = (expected: number) =>
    host.active() && section.isConnected && expected === operation;
  async function load(): Promise<void> {
    const expected = ++operation;
    const response = await host.api<{ items: Tile[] }>("GET", "/tiles");
    if (!active(expected) || response.status !== 200 || !Array.isArray(response.body?.items))
      return;
    const items = response.body.items.slice(0, 8);
    section.hidden = items.length === 0;
    section.replaceChildren();
    if (items.length === 0) return;
    const toolbar = node("div");
    toolbar.className = "cr-actions";
    const refresh = button("Refresh tiles");
    refresh.onclick = () => void load().catch(() => {});
    toolbar.append(refresh);
    section.append(toolbar);
    const grid = node("div");
    grid.className = "cr-tile-grid";
    section.append(grid);
    const outputs = new Map<string, HTMLElement>();
    for (const [index, tile] of items.entries()) {
      const card = node("article");
      card.className = "cr-personal-tile";
      const open = button(tile.available ? (tile.label ?? "Report") : "No longer available to you");
      open.disabled =
        !tile.available || tile.definitionId === undefined || tile.version === undefined;
      open.onclick = () => {
        if (tile.definitionId !== undefined && tile.version !== undefined)
          host.openSaved(tile.definitionId, tile.version);
      };
      const output = node("div");
      output.textContent = tile.available ? "Loading" : "";
      outputs.set(tile.id, output);
      card.append(open, output);
      if (host.canManage) {
        const controls = node("div");
        controls.className = "cr-actions";
        const move = async (direction: -1 | 1) => {
          const target = index + direction;
          if (target < 0 || target >= items.length) return;
          const ids = items.map((entry) => entry.id);
          const swapped = ids[index];
          ids[index] = ids[target] as string;
          ids[target] = swapped as string;
          const reply = await host.api("POST", "/tiles/order", { ids });
          if (!active(expected)) return;
          if (reply.status === 200) await load();
          else output.textContent = reply.body?.message ?? "Could not move the tile.";
        };
        const up = button("Up");
        up.disabled = index === 0;
        up.onclick = () => void move(-1).catch(() => {});
        const down = button("Down");
        down.disabled = index === items.length - 1;
        down.onclick = () => void move(1).catch(() => {});
        const remove = button("Remove tile");
        remove.onclick = () => {
          remove.disabled = true;
          void host
            .api("POST", `/tiles/${encodeURIComponent(tile.id)}/remove`)
            .then(async (reply) => {
              if (!active(expected)) return;
              if (reply.status === 200) await load();
              else output.textContent = reply.body?.message ?? "Could not remove the tile.";
            })
            .catch(() => {
              if (active(expected)) output.textContent = "Could not remove the tile.";
            })
            .finally(() => {
              remove.disabled = false;
            });
        };
        controls.append(up, down, remove);
        card.append(controls);
      }
      grid.append(card);
    }
    // Serial reads preserve the ordinary per-location concurrency rule.
    for (const tile of items) {
      if (!active(expected)) return;
      if (!tile.available) continue;
      const output = outputs.get(tile.id);
      if (output === undefined) continue;
      let reply: Reply<CustomReportResultDto>;
      try {
        reply = await host.api<CustomReportResultDto>(
          "POST",
          `/tiles/${encodeURIComponent(tile.id)}/run`,
        );
      } catch {
        if (!active(expected)) return;
        output.textContent = "-";
        continue;
      }
      if (!active(expected)) return;
      if (reply.status !== 200 || !Array.isArray(reply.body?.rows)) {
        output.textContent =
          reply.status === 403 || reply.status === 404 ? "No longer available to you" : "-";
        continue;
      }
      const result = reply.body;
      const columns = result.columns.filter(
        (candidateColumn) => candidateColumn.kind === "measure",
      );
      const compositeMeasure = selectedCompositeTileMeasure(result.spec);
      const isComposite = (result.spec as { specVersion?: unknown } | null)?.specVersion === 2;
      if (
        (isComposite ? compositeMeasure === undefined : columns.length !== 1) ||
        result.rows.length !== 1
      ) {
        output.textContent = "-";
        continue;
      }
      const column = isComposite
        ? columns.find((candidateColumn) => candidateColumn.key === compositeMeasure)
        : columns[0];
      if (column === undefined) {
        output.textContent = "-";
        continue;
      }
      const at = result.columns.indexOf(column);
      const value = result.rows[0]?.[at];
      const figure = node("p", formatCell(value ?? null, column.type));
      figure.className = "cr-tile-value";
      output.replaceChildren(figure);
      const comparison = compareCells(result, 0);
      const delta = isComposite
        ? comparison[columns.indexOf(column) * 2 + 1]
        : comparison.find((cell) => cell.part === "change");
      if (delta !== undefined) output.append(node("p", delta.text));
      output.append(node("p", dateSpanText(result.meta.from, result.meta.to)));
      const parsed = new Date(result.meta.generatedAt);
      if (Number.isFinite(parsed.getTime()))
        output.append(
          node("p", `As of ${parsed.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" })}`),
        );
    }
  }
  void load().catch(() => {
    if (host.active()) section.hidden = true;
  });
}
function selectedCompositeTileMeasure(spec: unknown): string | undefined {
  if (spec === null || typeof spec !== "object") return undefined;
  const raw = spec as Record<string, unknown>;
  if (
    raw["specVersion"] !== 2 ||
    !Array.isArray(raw["dimensions"]) ||
    raw["dimensions"].length !== 0
  )
    return undefined;
  const output = raw["output"];
  if (output === null || typeof output !== "object") return undefined;
  const tile = (output as Record<string, unknown>)["tile"];
  if (tile === null || typeof tile !== "object") return undefined;
  const selected = (tile as Record<string, unknown>)["measure"];
  if (typeof selected !== "string") return undefined;
  const sources = raw["sources"];
  if (!Array.isArray(sources)) return undefined;
  const namedSource = sources.some((source) => {
    if (source === null || typeof source !== "object") return false;
    const entry = source as Record<string, unknown>;
    return (
      Array.isArray(entry["measures"]) &&
      entry["measures"].some(
        (measure) =>
          measure !== null &&
          typeof measure === "object" &&
          `${String(entry["id"])}_${String((measure as Record<string, unknown>)["as"])}` ===
            selected,
      )
    );
  });
  const calculations = raw["calculations"];
  return namedSource ||
    (Array.isArray(calculations) &&
      calculations.some(
        (calculation) =>
          calculation !== null &&
          typeof calculation === "object" &&
          (calculation as Record<string, unknown>)["as"] === selected,
      ))
    ? selected
    : undefined;
}
function tileSlot(spec: unknown): boolean {
  if (spec === null || typeof spec !== "object") return false;
  const raw = spec as Record<string, unknown>;
  if (raw["specVersion"] === 2) return selectedCompositeTileMeasure(spec) !== undefined;
  const output = raw["output"];
  return (
    Array.isArray(raw["dimensions"]) &&
    raw["dimensions"].length === 0 &&
    Array.isArray(raw["measures"]) &&
    raw["measures"].length === 1 &&
    output !== null &&
    typeof output === "object" &&
    (output as Record<string, unknown>)["tile"] != null
  );
}
export function mountPinControl(
  hostElement: HTMLElement,
  host: PersonalTilesHost,
  input: { definitionId: string; version: number; spec: unknown },
): void {
  if (!host.canManage || !tileSlot(input.spec)) return;
  const control = button("Pin");
  hostElement.append(control);
  let pinned: Tile | undefined;
  let ready = false;
  async function refreshStatus(): Promise<void> {
    ready = false;
    control.disabled = true;
    try {
      const reply = await host.api<{ items: Tile[] }>("GET", "/tiles");
      if (!host.active() || !control.isConnected) return;
      if (reply.status !== 200 || !Array.isArray(reply.body?.items)) {
        control.textContent = "Retry pin status";
        return;
      }
      pinned = reply.body.items.find((tile) => tile.definitionId === input.definitionId);
      ready = true;
      control.textContent =
        pinned === undefined
          ? "Pin"
          : pinned.version === input.version
            ? "Pinned"
            : "Pin this version";
      control.setAttribute("aria-pressed", String(pinned?.version === input.version));
    } catch {
      if (host.active() && control.isConnected) control.textContent = "Retry pin status";
    } finally {
      control.disabled = false;
    }
  }
  void refreshStatus();
  control.onclick = () => {
    if (!ready) {
      void refreshStatus();
      return;
    }
    control.disabled = true;
    const path =
      pinned?.version === input.version
        ? `/tiles/${encodeURIComponent(pinned.id)}/remove`
        : "/tiles/pin";
    void host
      .api("POST", path, { definitionId: input.definitionId, version: input.version })
      .then(async (reply) => {
        if (!host.active() || !control.isConnected) return;
        if (reply.status === 200) await refreshStatus();
        else control.textContent = reply.body?.message ?? "Could not pin the report.";
      })
      .catch(() => {
        if (host.active() && control.isConnected) control.textContent = "Could not pin the report.";
      })
      .finally(() => {
        control.disabled = false;
      });
  };
}
/** REPORTDASH1: what one run of a personal tile shows on the Reports dashboard strip: the one figure, its change, its dates and when it was read. Undefined when the run is not a single figure. */
export interface TileFigure {
  value: string;
  delta?: string;
  span: string;
  asOf?: string;
}
export function tileFigure(result: CustomReportResultDto): TileFigure | undefined {
  if (!Array.isArray(result?.rows) || result.rows.length !== 1) return undefined;
  const columns = result.columns.filter((candidate) => candidate.kind === "measure");
  const compositeMeasure = selectedCompositeTileMeasure(result.spec);
  const isComposite = (result.spec as { specVersion?: unknown } | null)?.specVersion === 2;
  if (isComposite ? compositeMeasure === undefined : columns.length !== 1) return undefined;
  const column = isComposite ? columns.find((candidate) => candidate.key === compositeMeasure) : columns[0];
  if (column === undefined) return undefined;
  const value = result.rows[0]?.[result.columns.indexOf(column)];
  const comparison = compareCells(result, 0);
  const delta = isComposite ? comparison[columns.indexOf(column) * 2 + 1] : comparison.find((cell) => cell.part === "change");
  const parsed = new Date(result.meta.generatedAt);
  return {
    value: formatCell(value ?? null, column.type),
    ...(delta === undefined ? {} : { delta: delta.text }),
    span: dateSpanText(result.meta.from, result.meta.to),
    ...(Number.isFinite(parsed.getTime())
      ? { asOf: parsed.toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) }
      : {}),
  };
}
